import React, { useState } from 'react';
import { MessageCircle, Truck, CheckCircle, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { CONTACT_INFO } from '../data/mockData';

const { global } = siteContent;

const MiniMoves = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWhatsAppClick = (templateName, variables = {}) => {
    trackWhatsAppClick({ templateName, page: 'mini-moves' });
    const link = generateWhatsAppLink(templateName, variables);
    window.open(link, '_blank');
  };

  const packages = [
    {
      title: 'Driver Only',
      description: 'Perfect for light items you can help load',
      features: ['1-2 small furniture pieces', 'Up to 10 boxes', 'You assist with loading/unloading']
    },
    {
      title: 'Driver + 1 Helper',
      description: 'Most popular for standard moves',
      features: ['Full room of furniture', 'Up to 20 boxes', 'Helper assists with heavy lifting']
    },
    {
      title: 'Driver + 2 Helpers',
      description: 'For larger loads or difficult access',
      features: ['Multiple rooms', 'Heavy appliances', 'Stairs or complex access']
    }
  ];

  const pricingFactors = [
    { icon: 'MapPin', text: 'Distance between locations' },
    { icon: 'Package', text: 'Load size and weight' },
    { icon: 'Building', text: 'Stairs and lift access' },
    { icon: 'Clock', text: 'Waiting time at pickup/drop-off' },
    { icon: 'Route', text: 'Number of extra stops' }
  ];

  const careFirst = [
    'Moving blankets for furniture protection',
    'Straps for secure loading',
    'Respectful, professional service',
    'Photo proof at pickup and delivery'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      
      const response = await apiService.submitMiniMoveQuote(data);
      toast.success(`Quote submitted! Reference: ${response.quote_id}. Send photos on WhatsApp!`);
      
      // Open WhatsApp with form data
      setTimeout(() => {
        handleWhatsAppClick('miniMove', { ...data, refId: response.quote_id });
      }, 1500);
      
      e.target.reset();
    } catch (error) {
      toast.error('Failed to submit. Please try WhatsApp instead.');
      handleWhatsAppClick('miniMove', {});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Truck className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Mini-Moves & Small Removals</h1>
            <p className="text-xl text-gray-300 mb-8">
              From a single couch to a full bachelor apartment - we handle it with care
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8"
                onClick={() => handleWhatsAppClick('miniMove', {})}
                data-testid="minimove-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp for Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#001A47]"
                onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Choose Your Package</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <Card key={index} className="hover:shadow-xl transition-all border-2 hover:border-[#039fff]">
                <CardContent className="p-6">
                  <Users className="h-12 w-12 text-[#fb6402] mb-4" />
                  <h3 className="text-2xl font-bold text-[#001A47] mb-3">{pkg.title}</h3>
                  <p className="text-gray-600 mb-4">{pkg.description}</p>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="h-4 w-4 text-[#039fff] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Factors */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">What Affects Your Price?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingFactors.map((factor, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <div className="bg-[#039fff]/10 rounded-full p-3">
                  <CheckCircle className="h-6 w-6 text-[#039fff]" />
                </div>
                <p className="text-gray-700">{factor.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-8 max-w-2xl mx-auto">
            We provide transparent quotes before you book. Send us photos on WhatsApp for the most accurate estimate.
          </p>
        </div>
      </section>

      {/* Care First */}
      <section className="py-16 bg-[#039fff]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">Care-First Handling</h2>
            <p className="text-lg text-gray-600">
              We treat your belongings like our own
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {careFirst.map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-6 rounded-lg shadow-md">
                <CheckCircle className="h-6 w-6 text-[#fb6402] flex-shrink-0 mt-1" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#001A47] mb-4">Get Your Mini-Move Quote</h2>
              <p className="text-gray-600">Send us photos on WhatsApp for the most accurate quote</p>
            </div>

            <Card>
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickup">Pickup Location</Label>
                      <Input id="pickup" placeholder="e.g., Stellenbosch" required />
                    </div>
                    <div>
                      <Label htmlFor="dropoff">Drop-off Location</Label>
                      <Input id="dropoff" placeholder="e.g., Cape Town CBD" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="items">Item List</Label>
                    <Textarea id="items" placeholder="List furniture, appliances, boxes..." rows={4} required />
                    <p className="text-xs text-gray-500 mt-1">Or send photos via WhatsApp after submitting</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="floors">Floors/Lift Access</Label>
                      <Input id="floors" placeholder="e.g., 2nd floor, no lift" required />
                    </div>
                    <div>
                      <Label htmlFor="helpers">Helpers Needed</Label>
                      <select id="helpers" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required>
                        <option value="">Select...</option>
                        <option>Driver only</option>
                        <option>Driver + 1 helper</option>
                        <option>Driver + 2 helpers</option>
                        <option>Not sure (help me decide)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="datetime">Preferred Date/Time</Label>
                      <Input id="datetime" type="datetime-local" required />
                    </div>
                    <div>
                      <Label htmlFor="name">Your Name</Label>
                      <Input id="name" placeholder="Full name" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input id="mobile" type="tel" placeholder="+27" required />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                      Submit & Send Photos
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleWhatsAppClick('miniMove', {})}
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      WhatsApp Directly
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Moving Regularly?</h2>
          <p className="text-xl text-gray-300 mb-8">Business accounts get priority scheduling and monthly billing</p>
          <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02]" onClick={() => window.location.href = '/business'}>
            Explore Business Accounts
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default MiniMoves;
